import type { FallbackStep, FieldRule } from "@productlane/lib"

type ChainRule = Extract<FieldRule, { mode: "ref" | "template" }>

export type StepMode = FallbackStep["mode"]

/** In the order the chain runs. A fixed value ends it, so it can only sit last. */
export const STEP_MODES: readonly StepMode[] = ["ref", "template", "fixed"]

/** One step of the chain, flattened so step 1 and its fallbacks read alike. */
export interface ChainStep {
	mode: StepMode
	value: string
}

export function blankStep(mode: StepMode): FallbackStep {
	if (mode === "ref") return { mode: "ref", ref: "" }
	if (mode === "template") return { mode: "template", template: "" }
	return { mode: "fixed", value: "" }
}

export function stepValue(step: FallbackStep): string {
	if (step.mode === "ref") return step.ref
	return step.mode === "template" ? step.template : step.value
}

export function withStepValue(step: FallbackStep, next: string): FallbackStep {
	if (step.mode === "ref") return { mode: "ref", ref: next }
	if (step.mode === "template") return { mode: "template", template: next }
	return { mode: "fixed", value: next }
}

/** Step 1 is the rule's own value; the rest are its fallbacks. */
export function chainSteps(rule: ChainRule): ChainStep[] {
	const head: ChainStep = {
		mode: rule.mode,
		value: rule.mode === "ref" ? rule.ref : rule.template,
	}
	return [
		head,
		...(rule.fallbacks ?? []).map((step) => ({
			mode: step.mode,
			value: stepValue(step),
		})),
	]
}

/** Folds the flattened steps back into the rule, cast and all. */
export function applySteps(rule: ChainRule, steps: ChainStep[]): FieldRule {
	const [head, ...rest] = steps
	const value = head?.value ?? ""
	const fallbacks = rest.map((step) =>
		withStepValue(blankStep(step.mode), step.value),
	)
	const carried = fallbacks.length > 0 ? { fallbacks } : {}
	const casted = rule.cast ? { cast: rule.cast } : {}

	return rule.mode === "ref"
		? { mode: "ref", ref: value, ...casted, ...carried }
		: { mode: "template", template: value, ...casted, ...carried }
}

export function setStep(
	rule: ChainRule,
	index: number,
	patch: Partial<ChainStep>,
): FieldRule {
	const steps = chainSteps(rule)
	const current = steps[index]
	if (!current) return rule
	// A mode swap starts the step over, the way picking a rule mode does.
	steps[index] = patch.mode
		? { mode: patch.mode, value: patch.value ?? "" }
		: { ...current, ...patch }
	return applySteps(rule, steps)
}

export function removeStep(rule: ChainRule, index: number): FieldRule {
	return applySteps(
		rule,
		chainSteps(rule).filter((_, at) => at !== index),
	)
}

export function addStep(rule: ChainRule): FieldRule {
	const steps = chainSteps(rule)
	return applySteps(rule, [...steps, { mode: rule.mode, value: "" }])
}
